
import { useState, useEffect } from "react";
import { supabase } from "../integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { clearAuthStorage } from "@/utils/authCleanup";

export const useSession = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    // Helper to safely set session
    const safeSetSession = (newSession: Session | null) => {
      if (!isMounted) return;
      
      // Only update if session is different
      if (JSON.stringify(newSession) !== JSON.stringify(session)) {
        console.log("Session updated:", newSession ? "authenticated" : "unauthenticated");
        setSession(newSession);
        
        // Clear loading and error
        setLoading(false);
        setError(null);
        
        // Reset retry counter on successful session
        retryCount = 0;
      }
    };

    // Helper to safely handle errors
    const safeSetError = (err: Error) => {
      if (!isMounted) return;
      console.error("Session error:", err);
      
      retryCount++;
      if (retryCount >= MAX_RETRIES) {
        console.log(`Max retries (${MAX_RETRIES}) reached, clearing auth storage`);
        clearAuthStorage(queryClient);
        setError(err);
        setLoading(false);
      } else {
        console.log(`Authentication retry ${retryCount}/${MAX_RETRIES}`);
        // Only set loading to false after max retries
        if (retryCount >= MAX_RETRIES) {
          setLoading(false);
        }
      }
      
      // Notify user of issues
      toast({
        title: "Authentication error",
        description: "Please try logging out and back in",
        variant: "destructive",
      });
    };

    // Helper to validate a session token specifically
    const validateSessionToken = (sessionToValidate: Session | null): boolean => {
      if (!sessionToValidate) return false;
      
      try {
        // Basic validation checks
        if (!sessionToValidate.access_token || !sessionToValidate.refresh_token) {
          console.warn("Invalid session: missing tokens");
          return false;
        }
        
        // Check if token is expired
        const expiresAt = sessionToValidate.expires_at;
        if (!expiresAt || expiresAt < Math.floor(Date.now() / 1000)) {
          console.warn("Invalid session: token expired");
          return false;
        }
        
        return true;
      } catch (err) {
        console.error("Error validating token:", err);
        return false;
      }
    };

    // Set up auth state change listener with error handling
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, newSession) => {
          try {
            console.log("Auth state changed:", event);
            
            // Validate the session token before processing
            if (newSession && !validateSessionToken(newSession)) {
              console.warn("Invalid session detected during auth state change, clearing auth storage");
              clearAuthStorage(queryClient);
              safeSetSession(null);
              return;
            }
            
            // Handle sign out explicitly
            if (event === 'SIGNED_OUT' || !newSession) {
              clearAuthStorage(queryClient);
              
              // If this was an explicit sign out, redirect to home
              if (event === 'SIGNED_OUT') {
                window.location.href = '/';
              }
            }
            
            safeSetSession(newSession);
          } catch (err) {
            safeSetError(err instanceof Error ? err : new Error(String(err)));
          }
        }
      );

      // Initial session check with error handling and validation
      supabase.auth.getSession().then(({ data: { session: initialSession }, error: sessionError }) => {
        if (sessionError) {
          safeSetError(new Error(sessionError.message));
        } else if (initialSession && !validateSessionToken(initialSession)) {
          console.warn("Invalid initial session detected, clearing auth storage");
          clearAuthStorage(queryClient);
          safeSetSession(null);
        } else {
          safeSetSession(initialSession);
        }
      }).catch(err => {
        safeSetError(err instanceof Error ? err : new Error(String(err)));
      });

      // Clean up subscription
      return () => {
        isMounted = false;
        subscription.unsubscribe();
      };
    } catch (err) {
      safeSetError(err instanceof Error ? err : new Error(String(err)));
      return () => { isMounted = false; };
    }
  }, [session, queryClient]);

  return { 
    session, 
    loading, 
    error, 
    clearAuth: () => clearAuthStorage(queryClient) 
  };
};

export default useSession;
